import { useCallback, useState } from 'react'

import { Card } from '@loadsmart/loadsmart-ui'
import { Banner, Text } from '@loadsmart/miranda-react'

import { useHiddenOnScroll } from 'core/ui/hooks/useHiddenComponent'

import { type Proposal } from '../domain/Proposal'
import { RfpConfirmationDialog, type RfpConfirmationDialogProps } from './RfpConfirmationDialog'
import { ActionsWrapper } from './RfpDetails.styles'
import { RfpDetailsHeader } from './RfpDetailsHeader'
import { RfpInformation } from './RfpInformation'
import { RfpRatesButton } from './RfpRatesButton'

type GetDialogPropsType = {
  isSubmitDialogOpen: boolean
  isWithdrawDialogOpen: boolean
  isChangeDialogOpen: boolean
  isReviewAccessorialsDialogOpen: boolean
  isFillAccessorialRatesDialogOpen: boolean
  onChange: () => void
  onChangeCancel: () => void
  onWithdraw: () => void
  onWithdrawCancel: () => void
  onSubmit: () => void
  onSubmitCancel: () => void
  onReviewAccessorialsTab: () => void
  onReviewAccessorialsTabCancel: () => void
  onFillAccessorialRates: () => void
  onFillAccessorialRatesCancel: () => void
}

function getDialogProps({
  isSubmitDialogOpen,
  isWithdrawDialogOpen,
  isChangeDialogOpen,
  isReviewAccessorialsDialogOpen,
  isFillAccessorialRatesDialogOpen,
  onChange,
  onChangeCancel,
  onWithdraw,
  onWithdrawCancel,
  onSubmit,
  onSubmitCancel,
  onReviewAccessorialsTab,
  onReviewAccessorialsTabCancel,
  onFillAccessorialRates,
  onFillAccessorialRatesCancel,
}: GetDialogPropsType): RfpConfirmationDialogProps | undefined {
  if (isChangeDialogOpen) {
    return {
      title: 'Submit offer?',
      body: 'You can withdraw your offer before the bid deadline expires',
      onConfirm: onChange,
      onCancel: onChangeCancel,
    }
  }

  if (isWithdrawDialogOpen) {
    return {
      title: 'Withdraw offer?',
      body: 'You can withdraw your offer before the bid deadline expires',
      confirmLabel: 'Withdraw',
      onConfirm: onWithdraw,
      onCancel: onWithdrawCancel,
    }
  }

  if (isSubmitDialogOpen) {
    return {
      title: 'Submit Rates',
      body: 'Submitted rates cannot be edited.',
      confirmLabel: 'Yes, submit rates',
      cancelLabel: 'No, back to page',
      onConfirm: onSubmit,
      onCancel: onSubmitCancel,
    }
  }

  if (isReviewAccessorialsDialogOpen) {
    return {
      title: 'One Last Thing',
      body: (
        <Text>
          You haven't reviewed the extra charges information available on Accessorials tab.{' '}
          <Text variant="body-md-bold">Are you sure you want to submit?</Text>
        </Text>
      ),
      confirmLabel: 'Yes, submit',
      cancelLabel: 'No, back to page',
      onConfirm: onReviewAccessorialsTab,
      onCancel: onReviewAccessorialsTabCancel,
    }
  }

  if (isFillAccessorialRatesDialogOpen) {
    return {
      title: 'One Last Thing',
      body: (
        <Text>
          You haven't filled out all the accessorials rates.{' '}
          <Text variant="body-md-bold">Are you sure you want to submit?</Text>
        </Text>
      ),
      confirmLabel: 'Yes, submit',
      cancelLabel: 'No, back to page',
      onConfirm: onFillAccessorialRates,
      onCancel: onFillAccessorialRatesCancel,
    }
  }
}

export type RfpDetailsProps = {
  rfpId: string
  proposal: Proposal
  autoSubmit: boolean
  disabled: boolean
  isBidDisabled: boolean
  isSubmittingProposal: boolean
  isAutoSubmittingProposal: boolean
  isWithdrawingProposal: boolean
  onSubmitProposal: () => void
  onWithdrawProposal: () => void
  onToggleAutoSubmitProposal: (autoSubmit: boolean) => void
  onChangeProposal: () => void
  onFileUploaded: () => void
  hasReviewedAccessorialsTab?: boolean
  hasFilledAccessorialRate?: boolean
}

export function RfpDetails({
  rfpId,
  proposal,
  autoSubmit = false,
  disabled = false,
  isBidDisabled = false,
  isSubmittingProposal,
  isAutoSubmittingProposal,
  isWithdrawingProposal,
  onFileUploaded,
  onSubmitProposal,
  onWithdrawProposal,
  onToggleAutoSubmitProposal,
  onChangeProposal,
  hasReviewedAccessorialsTab,
  hasFilledAccessorialRate,
}: RfpDetailsProps): JSX.Element {
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState<boolean>(false)
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState<boolean>(false)
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState<boolean>(false)
  const [isReviewAccessorialsDialogOpen, setIsReviewAccessorialsDialogOpen] =
    useState<boolean>(false)
  const [isFillAccessorialRatesDialogOpen, setIsFillAccessorialRatesDialogOpen] =
    useState<boolean>(false)
  const [hasUploadTimeoutError, setHasUploadTimeoutError] = useState(false)
  const { hidden } = useHiddenOnScroll('rfp-bid-card-separator')

  const isRfpDialogOpen =
    isSubmitDialogOpen ||
    isWithdrawDialogOpen ||
    isChangeDialogOpen ||
    isReviewAccessorialsDialogOpen ||
    isFillAccessorialRatesDialogOpen

  const { rfp, rateType } = proposal

  const { name } = rfp

  const handleOnSubmitConfirm = useCallback(() => {
    onSubmitProposal()
    setIsSubmitDialogOpen(false)
  }, [onSubmitProposal])

  const handleOnSubmitCancel = useCallback(() => {
    setIsSubmitDialogOpen(false)
  }, [])

  const handleOnWithdrawConfirm = useCallback(() => {
    onWithdrawProposal()
    setIsWithdrawDialogOpen(false)
  }, [onWithdrawProposal])

  const handleOnWithdrawCancel = useCallback(() => {
    setIsWithdrawDialogOpen(false)
  }, [])

  const handleOnChangeConfirm = useCallback(() => {
    onChangeProposal()
    setIsChangeDialogOpen(false)
  }, [onChangeProposal])

  const handleOnChangeCancel = useCallback(() => {
    setIsChangeDialogOpen(false)
  }, [])

  const handleReviewAccessorialsTabCancel = useCallback(() => {
    setIsReviewAccessorialsDialogOpen(false)
  }, [])

  const handleOnFillAccessorialRatesCancel = useCallback(() => {
    setIsFillAccessorialRatesDialogOpen(false)
  }, [])

  const onRfpSubmitButtonClick = useCallback(() => {
    if (!hasReviewedAccessorialsTab) {
      setIsReviewAccessorialsDialogOpen(true)
    } else if (!hasFilledAccessorialRate) {
      setIsFillAccessorialRatesDialogOpen(true)
    } else {
      setIsSubmitDialogOpen(true)
    }
  }, [hasFilledAccessorialRate, hasReviewedAccessorialsTab])

  const dialogProps = getDialogProps({
    isSubmitDialogOpen,
    isWithdrawDialogOpen,
    isChangeDialogOpen,
    isReviewAccessorialsDialogOpen,
    isFillAccessorialRatesDialogOpen,
    onSubmit: handleOnSubmitConfirm,
    onSubmitCancel: handleOnSubmitCancel,
    onChange: handleOnChangeConfirm,
    onChangeCancel: handleOnChangeCancel,
    onWithdraw: handleOnWithdrawConfirm,
    onWithdrawCancel: handleOnWithdrawCancel,
    onReviewAccessorialsTab: handleOnSubmitConfirm,
    onReviewAccessorialsTabCancel: handleReviewAccessorialsTabCancel,
    onFillAccessorialRates: handleOnSubmitConfirm,
    onFillAccessorialRatesCancel: handleOnFillAccessorialRatesCancel,
  })

  return (
    <>
      {hasUploadTimeoutError && (
        <Banner variant="warning" dismissible>
          <Banner.Description>
            Your file is being processed in the background due to its size.{' '}
            <b>Please refresh in a few minutes.</b>
          </Banner.Description>
        </Banner>
      )}
      <Card>
        {isRfpDialogOpen && dialogProps && <RfpConfirmationDialog {...dialogProps} />}
        <RfpDetailsHeader
          autoSubmit={autoSubmit}
          autoSubmitDisabled={disabled || isAutoSubmittingProposal || isSubmittingProposal}
          fixed={hidden}
          loading={isSubmittingProposal || isWithdrawingProposal}
          proposal={proposal}
          submitDisabled={disabled}
          onChangeProposal={() => setIsChangeDialogOpen(true)}
          onSubmit={onRfpSubmitButtonClick}
          onWithdraw={() => setIsWithdrawDialogOpen(true)}
          onToggleAutoSubmit={onToggleAutoSubmitProposal}
        />
        <Card.Separator className="rfp-bid-card-separator" />
        <RfpInformation
          disabled={disabled}
          proposal={proposal}
          RfpRatesButton={
            <ActionsWrapper>
              <RfpRatesButton
                isUploadDisabled={isBidDisabled || disabled}
                rfpId={rfpId}
                rfpName={name}
                rateType={rateType}
                distanceUnit={rfp.distanceUnit}
                onFileUploaded={onFileUploaded}
                setUploadTimeoutError={() => setHasUploadTimeoutError(true)}
              />
            </ActionsWrapper>
          }
        />
        <Card.Separator />
      </Card>
    </>
  )
}
